import { motion } from "framer-motion";
import { Check, Users, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_IMAGES } from "../../data/holidays";
import { LocalizedLink } from "../../lib/i18nRouting";
import { Chapter, Reveal, EnquiryButton, EASE } from "./shared";

export function HolidaySeason() {
  const { t } = useTranslation();
  const months = t("holidays.season.months", { returnObjects: true });
  return (
    <section className="relative bg-forest-deep text-bone py-28 lg:py-44 overflow-hidden" data-testid="holidays-season">
      <img src={HOLIDAY_IMAGES.season} alt={t("holidays.season.alt")} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-b from-forest-deep via-forest-deep/70 to-forest-deep" />
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <Chapter number={t("holidays.season.chapter")} label={t("holidays.season.eyebrow")} tone="dark" />
        <Reveal delay={0.1}>
          <h2 className="font-display leading-[0.9] mt-8 uppercase tracking-tight text-[16vw] sm:text-8xl lg:text-[10rem]">
            Summer <span className="italic normal-case">2027</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2} className="mt-6 text-base md:text-lg text-bone/80 max-w-xl">{t("holidays.season.p1")}</Reveal>

        <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-4" data-testid="season-months">
          {months.map((m, i) => (
            <motion.span
              key={m}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.1 }}
              className="flex items-center gap-6 font-display text-3xl sm:text-4xl lg:text-6xl uppercase"
            >
              {m}
              {i < months.length - 1 && <span className="w-2 h-2 bg-terracotta rounded-full" />}
            </motion.span>
          ))}
          <span className="font-display text-3xl sm:text-4xl lg:text-6xl italic text-bone/60">{t("holidays.season.year")}</span>
        </div>

        <Reveal delay={0.3} className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px bg-bone/10 border border-bone/15 max-w-3xl">
          <Stat icon={<Users className="w-4 h-4" />} label={t("holidays.season.groups_label")} value={t("holidays.season.min")} />
          <Stat icon={<Users className="w-4 h-4" />} label={t("holidays.season.groups_label")} value={t("holidays.season.max")} />
          <Stat icon={<ShieldCheck className="w-4 h-4" />} label="" value={t("holidays.season.guarantee")} accent />
        </Reveal>
      </div>
    </section>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="bg-forest-deep/80 backdrop-blur p-6 flex flex-col gap-3">
      <span className={accent ? "text-terracotta" : "text-bone/60"}>{icon}</span>
      {label && <span className="text-[10px] uppercase tracking-wider-3 text-bone/50">{label}</span>}
      <span className={`text-sm md:text-base ${accent ? "text-bone" : "text-bone/90"}`}>{value}</span>
    </div>
  );
}

export function HolidayPrivate() {
  const { t } = useTranslation();
  const items = t("holidays.private.items", { returnObjects: true });
  return (
    <section className="bg-bone py-24 lg:py-40" data-testid="holidays-private">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <Chapter number={t("holidays.private.chapter")} label={t("holidays.private.eyebrow")} />
          <Reveal delay={0.1}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-forest leading-[1.0] mt-8">
              Your group.<br /><span className="italic">Your Apuseni.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            <p className="text-forest text-xl">{t("holidays.private.p1")}</p>
            <p>{t("holidays.private.p2")}</p>
          </Reveal>
          <Reveal delay={0.3} className="mt-10">
            <EnquiryButton variant="dark" testid="private-cta" pkg="custom">{t("holidays.private.cta")}</EnquiryButton>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal className="hol-frame-alt relative aspect-[16/9] overflow-hidden bg-stoneWarm group">
            <img src={HOLIDAY_IMAGES.private} alt={t("holidays.private.alt")} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
          </Reveal>
          <Reveal delay={0.1} className="mt-8 text-xs uppercase tracking-wider-3 text-moss">{t("holidays.private.p3")}</Reveal>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {items.map((it, i) => (
              <motion.li
                key={it}
                data-testid={`private-item-${i}`}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
                className="flex items-center gap-3 py-3 border-b border-bone-deep text-forest text-sm md:text-base"
              >
                <Check className="w-4 h-4 text-terracotta shrink-0" /> {it}
              </motion.li>
            ))}
          </ul>
          <Reveal delay={0.1} className="mt-10 text-xs uppercase tracking-wider-3 text-moss">{t("holidays.private.related_label")}</Reveal>
          <nav aria-label="Related pages" className="mt-3 flex flex-col gap-2 text-sm">
            <LocalizedLink to="/experiente" data-testid="holidays-link-tours" className="text-forest link-underline self-start">{t("holidays.private.related_tours")}</LocalizedLink>
            <LocalizedLink to="/apuseni" data-testid="holidays-link-apuseni" className="text-forest link-underline self-start">{t("holidays.private.related_apuseni")}</LocalizedLink>
          </nav>
        </div>
      </div>
    </section>
  );
}
